import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import {
  Upload, FileSpreadsheet, FileText, Loader2, Search,
  CheckCircle2, XCircle, AlertCircle, Sparkles,
} from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const useApi = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  return axios.create({ baseURL: API_BASE, headers: { Authorization: `Bearer ${token}` } });
};

export default function SeatBulkClearanceDialog({ open, onClose, onComplete }) {
  const api = useApi();
  const [step, setStep] = useState('input'); // 'input' | 'preview' | 'result'
  const [textInput, setTextInput] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [action, setAction] = useState('approve');
  const [busy, setBusy] = useState(false);
  const [previewFilter, setPreviewFilter] = useState('all'); // all|matched|unmatched
  const [selectedRows, setSelectedRows] = useState(new Set());
  const fileRef = useRef(null);

  const reset = () => {
    setStep('input'); setTextInput(''); setPreview(null); setResult(null);
    setAction('approve'); setBusy(false); setPreviewFilter('all');
    setSelectedRows(new Set());
  };
  const handleClose = () => { reset(); onClose(); };

  // ITS eligible for the current action (matched + status not already final)
  const eligibleITS = React.useMemo(() => {
    if (!preview?.rows) return [];
    if (action === 'approve') {
      return preview.rows.filter(r => r.found && r.current_status !== 'Approved').map(r => r.its);
    }
    // remove: only currently approved rows make sense
    return preview.rows.filter(r => r.found && r.current_status === 'Approved').map(r => r.its);
  }, [preview, action]);

  // When preview loads or action changes, auto-select all eligible rows
  React.useEffect(() => {
    setSelectedRows(new Set(eligibleITS));
  }, [eligibleITS]);

  const toggleRow = (its) => {
    setSelectedRows(prev => {
      const n = new Set(prev);
      if (n.has(its)) n.delete(its); else n.add(its);
      return n;
    });
  };
  const selectAllVisible = () => setSelectedRows(new Set(eligibleITS));
  const clearSelection = () => setSelectedRows(new Set());

  const splitInput = (s) => (s || '')
    .split(/[\s,;|]+/g).map(x => x.trim()).filter(Boolean);

  const submitText = async () => {
    const list = splitInput(textInput);
    if (list.length === 0) { toast.error('Paste at least one ITS number'); return; }
    setBusy(true);
    try {
      const res = await api.post('/seat/clearance/bulk-preview', { its_numbers: list });
      setPreview(res.data);
      setStep('preview');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed'); }
    finally { setBusy(false); }
  };

  const submitFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    setBusy(true);
    try {
      const res = await api.post('/seat/clearance/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      setStep('preview');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const applyAction = async () => {
    if (!preview) return;
    const list = Array.from(selectedRows);
    if (list.length === 0) { toast.error('Select at least one ITS to process'); return; }
    if (!window.confirm(`${action === 'approve' ? 'Approve' : 'Remove clearance for'} ${list.length} ITS number(s)?\nThis cannot be auto-reverted.`)) return;
    setBusy(true);
    try {
      const res = await api.post('/seat/clearance/bulk-apply', { its_numbers: list, action });
      setResult(res.data);
      setStep('result');
      onComplete?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk apply failed'); }
    finally { setBusy(false); }
  };

  const downloadReport = async (format) => {
    if (!result?.results?.length) return;
    try {
      const res = await api.post(`/seat/clearance/bulk-export?format=${format}`, { results: result.results }, { responseType: 'blob' });
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const mime = format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf';
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url; a.download = `bulk_clearance_${new Date().toISOString().slice(0, 10)}.${ext}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const filteredPreviewRows = preview?.rows?.filter(r =>
    previewFilter === 'all' ? true :
    previewFilter === 'matched' ? r.found :
    !r.found
  ) || [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-3xl" data-testid="bulk-clearance-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-600" /> Bulk HOF Clearance</DialogTitle>
        </DialogHeader>

        {step === 'input' && (
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-900 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>Paste ITS numbers below (comma / new-line / Excel-copy all work), or upload a sheet. We'll resolve each ITS → HOF and let you preview before applying.</div>
            </div>
            <div>
              <Label>Paste ITS numbers</Label>
              <textarea
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                rows={8}
                className="w-full border rounded px-2 py-1.5 text-sm font-mono mt-1"
                placeholder={"50462579\n50427791\n50412345\n50498765"}
                data-testid="bulk-paste"
              />
              <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                <span>{splitInput(textInput).length} entries detected</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={submitText} disabled={busy || splitInput(textInput).length === 0} data-testid="bulk-text-submit">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                Preview Pasted List
              </Button>
              <div className="text-xs text-slate-400">OR</div>
              <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-purple-600 text-white text-sm font-medium cursor-pointer hover:bg-purple-700">
                <Upload className="w-3 h-3" /> Upload .xlsx / .csv
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={submitFile} className="hidden" data-testid="bulk-file" />
              </label>
            </div>
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs">
              <div className="p-2 bg-slate-50 rounded"><div className="font-bold text-slate-800 text-lg">{preview.counts.total_input}</div>Total Input</div>
              <div className="p-2 bg-emerald-50 rounded"><div className="font-bold text-emerald-700 text-lg">{preview.counts.matched}</div>Matched</div>
              <div className="p-2 bg-amber-50 rounded"><div className="font-bold text-amber-700 text-lg">{preview.counts.unmatched}</div>Not Found</div>
              <div className="p-2 bg-blue-50 rounded"><div className="font-bold text-blue-700 text-lg">{preview.counts.already_cleared}</div>Already Cleared</div>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">Action:</Label>
              <div className="inline-flex rounded-md border bg-white">
                <button onClick={() => setAction('approve')}
                  className={`px-3 py-1 text-xs ${action === 'approve' ? 'bg-emerald-600 text-white' : 'text-slate-700'}`}
                  data-testid="bulk-action-approve">Approve</button>
                <button onClick={() => setAction('remove')}
                  className={`px-3 py-1 text-xs ${action === 'remove' ? 'bg-red-500 text-white' : 'text-slate-700'}`}
                  data-testid="bulk-action-remove">Remove</button>
              </div>
              <div className="flex-1" />
              <div className="inline-flex rounded-md border bg-white text-xs">
                {[['all', 'All'], ['matched', 'Matched'], ['unmatched', 'Not Found']].map(([v, l]) => (
                  <button key={v} onClick={() => setPreviewFilter(v)}
                    className={`px-2 py-1 ${previewFilter === v ? 'bg-slate-200' : ''}`}>{l}</button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 p-2 bg-purple-50 border border-purple-200 rounded">
              <Badge className="bg-purple-600">{selectedRows.size} selected</Badge>
              <span className="text-xs text-purple-900 flex-1">
                of {eligibleITS.length} eligible for <strong>{action === 'approve' ? 'approval' : 'removal'}</strong>
              </span>
              <Button size="sm" variant="outline" onClick={selectAllVisible} data-testid="bulk-select-all">Select All</Button>
              <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="bulk-select-none">Clear</Button>
            </div>

            <div className="border rounded max-h-72 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="px-2 py-1 w-8 text-center">
                      <input
                        type="checkbox"
                        aria-label="Select all eligible"
                        checked={eligibleITS.length > 0 && selectedRows.size === eligibleITS.length}
                        onChange={(e) => e.target.checked ? selectAllVisible() : clearSelection()}
                        data-testid="bulk-header-checkbox"
                      />
                    </th>
                    <th className="px-2 py-1 text-left">ITS</th><th className="px-2 py-1 text-left">Name</th><th className="px-2 py-1 text-left">HOF</th><th className="px-2 py-1 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredPreviewRows.map((r, i) => {
                    const isEligible = eligibleITS.includes(r.its);
                    return (
                    <tr key={i} className={!r.found ? 'bg-amber-50' : selectedRows.has(r.its) ? 'bg-emerald-50' : 'hover:bg-slate-50'}>
                      <td className="px-2 py-1 text-center">
                        <input
                          type="checkbox"
                          checked={selectedRows.has(r.its)}
                          onChange={() => toggleRow(r.its)}
                          disabled={!r.found}
                          title={!r.found
                            ? 'ITS not found in database'
                            : (isEligible ? '' : (action === 'approve' ? 'Already approved — selecting will skip' : 'Not currently approved — selecting will skip'))}
                          data-testid={`bulk-row-${r.its}`}
                        />
                      </td>
                      <td className="px-2 py-1 font-mono">{r.its}</td>
                      <td className="px-2 py-1 max-w-[200px] truncate">{r.name || '—'}</td>
                      <td className="px-2 py-1 max-w-[220px] truncate">{r.hof_name || '—'}</td>
                      <td className="px-2 py-1 text-center">
                        <Badge variant="outline" className={
                          r.current_status === 'Approved' ? 'text-emerald-700 border-emerald-300' :
                          r.current_status === 'Not Found' ? 'text-amber-700 border-amber-300' :
                          'text-slate-600'
                        }>{r.current_status}</Badge>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('input')}>Back</Button>
              <Button onClick={applyAction} disabled={busy || selectedRows.size === 0}
                className={action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-500 hover:bg-red-600'}
                data-testid="bulk-apply-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> :
                  (action === 'approve' ? <><CheckCircle2 className="w-4 h-4 mr-1" /> Approve {selectedRows.size} Selected</> :
                                          <><XCircle className="w-4 h-4 mr-1" /> Remove Clearance ({selectedRows.size})</>)}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'result' && result && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs">
              <div className="p-2 bg-emerald-50 rounded"><div className="font-bold text-emerald-700 text-lg">{result.counts.succeeded_rows}</div>Succeeded</div>
              <div className="p-2 bg-slate-50 rounded"><div className="font-bold text-slate-700 text-lg">{result.counts.skipped_rows}</div>Skipped</div>
              <div className="p-2 bg-amber-50 rounded"><div className="font-bold text-amber-700 text-lg">{result.counts.unmatched}</div>Not Found</div>
              <div className="p-2 bg-blue-50 rounded"><div className="font-bold text-blue-700 text-lg">{result.counts.processed_hofs}</div>HOFs Touched</div>
            </div>
            <Card>
              <CardContent className="p-0">
                <div className="max-h-72 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr><th className="px-2 py-1 text-left">ITS</th><th className="px-2 py-1 text-left">Name</th><th className="px-2 py-1 text-left">HOF</th><th className="px-2 py-1 text-left">Result</th></tr>
                    </thead>
                    <tbody className="divide-y">
                      {result.results.map((r, i) => (
                        <tr key={i}>
                          <td className="px-2 py-1 font-mono">{r.its}</td>
                          <td className="px-2 py-1 max-w-[180px] truncate">{r.name || '—'}</td>
                          <td className="px-2 py-1 max-w-[200px] truncate">{r.hof_name || '—'}</td>
                          <td className="px-2 py-1">
                            <Badge variant="outline" className={
                              r.result === 'Approved' || r.result === 'Removed' ? 'text-emerald-700 border-emerald-300' :
                              r.result?.startsWith('Skipped') ? 'text-slate-600' : 'text-amber-700 border-amber-300'
                            }>{r.result}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            <DialogFooter>
              <Button variant="outline" onClick={() => downloadReport('excel')} data-testid="bulk-export-excel">
                <FileSpreadsheet className="w-3 h-3 mr-1 text-green-600" /> Excel
              </Button>
              <Button variant="outline" onClick={() => downloadReport('pdf')} data-testid="bulk-export-pdf">
                <FileText className="w-3 h-3 mr-1 text-red-600" /> PDF
              </Button>
              <Button onClick={handleClose} data-testid="bulk-done">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
